import { post } from '@Green-Dot-Corporation/eureka-lib-fetch-utils';
import { v4 as uuidv4 } from 'uuid';
import {
    augmentHeaders,
    AUTHORIZATION_TOKEN_HEADER,
    parameterizeUrl,
    parseError,
} from './apiUtils';

export default {};

/**
 * to make a call we need authorizationToken in the header
 * to get authorizationToken, we need requestToken in the header
 * to get requestToken, we need recaptchaToken in the body or deviceToken in the header
 *
 * only startCampaignTracker API needs the requestToken - others need authorizationToken
 * when authorizationToken expires, we need to start all over again
 */

/**
 * This private function is only used in fetchRequestToken() when recaptchaToken is not present
 * @returns deviceToken
 */
const fetchDeviceToken = async () => {
    const body = JSON.stringify({ udid: `${Date.now()}-${uuidv4()}` });
    const { devicetoken: deviceToken } = await post(
        '/api/v1/auth/token/device',
        augmentHeaders({
            body,
        }),
    );

    return deviceToken;
};

/**
 * Returns a requestToken, which is used to retrieve authorizationToken.
 * This function is used in the pre-login case for 2 scenarios.
 *  1. without recaptcha, in which a deviceToken will be acquired from server.
 *  2. with recaptchaToken
 * @param {String} resourceUrl
 * @param {String} recaptchaToken
 * @returns requestToken
 */
export const fetchRequestToken = async (
    resourceUrl,
    recaptchaToken = '',
    iovationToken = '',
) => {
    const headers = {};

    if (!recaptchaToken) {
        const deviceToken = await fetchDeviceToken();
        headers[AUTHORIZATION_TOKEN_HEADER] = deviceToken;
    }

    // add recaptchaToken to body only when non-empty
    const body = {
        resourceUrl,
        ...(recaptchaToken && { recaptchatoken: recaptchaToken }),
        ...(iovationToken && { blackbox: iovationToken }),
        // TODO make this dynamic so we can use with other flows as well
        eventtype: 'HighHurdle',
    };

    const { requesttoken: requestToken } = await post(
        parameterizeUrl('/api/v1/auth/{programCode}/token/request'),
        augmentHeaders({
            headers,
            body: JSON.stringify(body),
        }),
    );

    return requestToken;
};

let preAuthorizationToken;

/**
 * This function reset the preAuthorizationToken so that it can be initialized or reset via other means.
 */
export const resetAuthorizationToken = (authToken = undefined) => {
    preAuthorizationToken = authToken;
};

export const authorizedFetch = async (method, url, options) => {
    try {
        const { headers: otherHeaders, ...rest } = options ?? {};
        const headers = {
            [AUTHORIZATION_TOKEN_HEADER]:
                preAuthorizationToken && `Bearer ${preAuthorizationToken}`,
            ...otherHeaders,
        };

        if (headers[AUTHORIZATION_TOKEN_HEADER]) {
            return await method(url, { headers, ...rest });
        }

        throw new Error(
            'Invalid Authorization - please initializeAuthorizationToken() first',
        );
    } catch (error) {
        return parseError(error);
    }
};

/**
 * Flow for signup
 * 1. call fetchRequestToken() to use for /enrollment/campaigntracking
 * 2. obtain recaptchaToken
 * 3. call fetchRequestToken(recaptchaToken) to get requestToken
 * 4. call initializeAuthorizationToken(requestToken)
 * 5. call other APIs using preAuthPost/preAuthGet (url, options)
 * - resetAuthorizationToken() and or call initializeAuthorizationToken() again to refresh the token as needed.
 */
